'use client'

import { useEffect, useRef } from 'react'
import RichText from '@/components/RichText'
import type { TapiEmbedBlock as TapiEmbedBlockProps } from '@/payload-types'

/**
 * Tapi embedded maintenance-request widget — same integration as the live site's
 * renter-information page: a .tapi-embedded-request mount plus Tapi's embed.js.
 */
export function TapiEmbedBlock({ companyId, sideHeading, sideContent }: TapiEmbedBlockProps) {
  const mounted = useRef(false)

  useEffect(() => {
    if (mounted.current) return
    mounted.current = true
    const id = 'tapi-embed-js'
    if (document.getElementById(id)) {
      // Script already present (client-side nav) — ask Tapi to rescan if it can
      ;(window as any).TapiEmbed?.init?.()
      return
    }
    const js = document.createElement('script')
    js.id = id
    js.src = 'https://tapi.app/js/embed.js'
    document.head.appendChild(js)
  }, [])

  if (!companyId) return null

  const hasSide = Boolean(sideHeading || sideContent)

  return (
    <section className="mx-auto max-w-[var(--container-content)] px-[var(--spacing-container)] pb-[var(--spacing-section-sm)] md:pb-[var(--spacing-section-md)]">
      {/* Figma 4112:88262 (desktop): two equal columns inside the 1472 content
          box — widget left, emergency copy right, 64px apart. Tablet 4112:88487
          keeps both columns at 32px apart; mobile 4112:88287 stacks them. */}
      <div
        className={hasSide ? 'grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-8 lg:gap-16' : undefined}
      >
        {/* The widget is a full-width iframe that centres its card — cap the mount at
            the card's width so it sits left-aligned per Figma. */}
        <div
          className="tapi-embedded-request w-full max-w-[480px]"
          data-company={companyId}
          data-type="bot"
        />

        {hasSide && (
          <div className="flex flex-col gap-8 lg:pr-16">
            {sideHeading && (
              /* Headings/headlineSm 4326:88515 — Merriweather 400, font/size/lg
                 24 / lineHeight/lg 32, letterSpacing -0.1. */
              <h2 className="font-display text-display-lg font-normal text-foreground">
                {sideHeading}
              </h2>
            )}
            {sideContent && (
              /* Body/bodyMd — Lato Light 300, 16/24, 24px paragraph spacing.
                 `prose-body-md` overrides the sitewide 18px prose scale. */
              <RichText
                data={sideContent}
                enableGutter={false}
                className="prose-body-md max-w-none"
              />
            )}
          </div>
        )}
      </div>
    </section>
  )
}
